import api from '@/services/api'

export type CsHealthBand = 'verde' | 'amarelo' | 'vermelho'

export type CsCarteiraStage =
  | 'CLIENTE_ATIVO'
  | 'EM_ACOMPANHAMENTO'
  | 'ATENCAO'
  | 'RISCO'
  | 'CRITICO'
  | 'PLANO_ACAO'
  | 'RETENCAO'

export type CsClassificacao = 'PRATA' | 'OURO' | 'DIAMANTE'

export type CsSectorHealth = {
  performance: number | null
  engajamento: number | null
  comunicacao: number | null
  score: number
}

export type CsUserRef = { id: string; name: string; email: string }

export type CsFinanceBlock = {
  mrr: number | null
  receita: number | null
  potencialExpansao: number | null
  impactoChurn: number | null
} | null

export interface CsAccountSummary {
  id: string
  status: string
  csStage: CsCarteiraStage
  classificacao: string | null
  planoContratado: string | null
  focalNome: string | null
  squadNome: string | null
  healthScore: number
  healthScoreFinal: number
  healthBand: CsHealthBand
  health: {
    marketing: CsSectorHealth
    suporte: CsSectorHealth
    projeto: CsSectorHealth
  }
  riscoChurn: boolean
  riscoMotivo: string | null
  riscoIdentificadoEm: string | null
  planoAcao: string | null
  planoAcaoStatus: string | null
  dataInicio: string
  dataRenovacao: string | null
  proximaReuniao: string | null
  ultimaReuniao: string | null
  proximoCheckIn: string | null
  linkGravacao: string | null
  linkAtaEkyte: string | null
  dorPrincipal: string | null
  ultimaInteracaoEm: string | null
  ltv?: number | null
  empresa: {
    id: string
    nome?: string | null
    razaoSocial?: string | null
    nomeFantasia?: string | null
    cnpj?: string | null
    email?: string | null
    telefone?: string | null
    status?: string
  }
  deal: {
    id: string
    nome: string
    servico?: string | null
    recorrencia?: string | null
    etapa?: string
    dataFechamento?: string | null
  }
  closer: CsUserRef
  csm: CsUserRef | null
  head: CsUserRef | null
  responsavelInterno: CsUserRef | null
  planoAcaoUser: CsUserRef | null
  finance: CsFinanceBlock
}

export interface CsDashboardData {
  totalAccounts: number
  greenCount: number
  yellowCount: number
  redCount: number
  riscoChurnCount: number
  semReuniao30d: number
  npsAvg: number | null
  upsellsEmAndamento: number
  crossSellsEmAndamento: number
}

export type CsTratativa = {
  id: string
  tipo: string
  status: string
  descricao: string
  resultado: string | null
  data: string
  createdAt: string
  responsavel: CsUserRef
}

export type CsExpansao = {
  id: string
  tipo: string
  potencial: number | null
  potencialNota: string | null
  oportunidade: string
  status: string
  createdAt: string
  updatedAt: string
}

export interface CsAccountDetail extends CsAccountSummary {
  proximaAcao: string | null
  observacoes: string | null
  analiseRelacionamento: string | null
  hasPortalToken: boolean
  nps: Array<{ id: string; score: number; comentario: string | null; respondidoEm: string }>
  interacoes: Array<{ id: string; tipo: string; descricao: string | null; resultado: string | null; data: string }>
  tratativas: CsTratativa[]
  expansao: CsExpansao[]
  onboarding: {
    id: string
    status: string
    tasks: Array<{ id: string; titulo: string; status: string; descricao: string | null }>
  } | null
}

export const CS_STAGE_LABELS: Record<CsCarteiraStage, string> = {
  CLIENTE_ATIVO: 'Cliente ativo',
  EM_ACOMPANHAMENTO: 'Em acompanhamento',
  ATENCAO: 'Atenção',
  RISCO: 'Risco',
  CRITICO: 'Crítico',
  PLANO_ACAO: 'Plano de ação',
  RETENCAO: 'Retenção',
}

export const CS_TRATATIVA_TIPOS = [
  { value: 'RECLAMACAO', label: 'Reclamação' },
  { value: 'RISCO_CHURN', label: 'Risco de churn' },
  { value: 'UPSELL', label: 'Upsell' },
  { value: 'CROSS_SELL', label: 'Cross-sell' },
  { value: 'SOLICITACAO', label: 'Solicitação do cliente' },
  { value: 'ALINHAMENTO', label: 'Alinhamento' },
  { value: 'PROBLEMA_OPERACIONAL', label: 'Problema operacional' },
] as const

export const CS_EXPANSAO_STATUS = [
  { value: 'IDENTIFICADA', label: 'Identificada' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'GANHA', label: 'Ganha' },
  { value: 'PERDIDA', label: 'Perdida' },
  { value: 'PAUSADA', label: 'Pausada' },
] as const

export const CS_PLANO_ACAO_STATUS = [
  { value: 'ABERTO', label: 'Aberto' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'CONCLUIDO', label: 'Concluído' },
  { value: 'CANCELADO', label: 'Cancelado' },
] as const

export async function fetchCsDashboard(): Promise<CsDashboardData> {
  const res = await api.get('/cs/dashboard')
  return res.data.data
}

export async function fetchCsAccounts(params?: {
  stage?: string
  riscoChurn?: boolean
  search?: string
  page?: number
}): Promise<{ data: CsAccountSummary[]; meta: { totalItems: number } }> {
  const res = await api.get('/cs/accounts', {
    params: {
      ...params,
      riscoChurn: params?.riscoChurn === undefined ? undefined : params.riscoChurn ? 'true' : 'false',
    },
  })
  return { data: res.data.data, meta: res.data.meta }
}

export async function fetchCsAccount(id: string): Promise<CsAccountDetail> {
  const res = await api.get(`/cs/accounts/${id}`)
  return res.data.data
}

export async function updateCsAccount(id: string, payload: Record<string, unknown>): Promise<CsAccountSummary> {
  const res = await api.patch(`/cs/accounts/${id}`, payload)
  return res.data.data
}

export async function createCsInteraction(
  id: string,
  payload: { tipo: string; descricao?: string; resultado?: string },
): Promise<void> {
  await api.post(`/cs/accounts/${id}/interactions`, payload)
}

export async function createCsNps(
  id: string,
  payload: { score: number; comentario?: string | null; respondidoEm?: string },
): Promise<void> {
  await api.post(`/cs/accounts/${id}/nps`, payload)
}

export async function createCsTratativa(
  id: string,
  payload: {
    tipo: string
    status?: string
    descricao: string
    resultado?: string
    responsavelUserId: string
    data?: string
  },
): Promise<void> {
  await api.post(`/cs/accounts/${id}/tratativas`, payload)
}

export async function createCsExpansao(
  id: string,
  payload: {
    tipo: 'UPSELL' | 'CROSS_SELL'
    potencial?: number | null
    potencialNota?: string | null
    oportunidade: string
    status?: string
  },
): Promise<void> {
  await api.post(`/cs/accounts/${id}/expansao`, payload)
}

export async function updateCsExpansao(
  id: string,
  expansaoId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await api.patch(`/cs/accounts/${id}/expansao/${expansaoId}`, payload)
}

export async function generateCsPortalToken(id: string): Promise<{ token: string }> {
  const res = await api.post(`/cs/accounts/${id}/portal-token`)
  return res.data.data
}
